import { NetworkStatus, useMutation, useQuery as useApolloQuery, useApolloClient } from '@apollo/client';
import { useIsMobile } from '@newfront-insurance/admin-ui';
import { graphql } from '@newfront-insurance/graphql-frontend';
import type { AuthProviderContext } from '@newfront-insurance/next-auth';
import { useQuery } from '@newfront-insurance/next-router-provider';
import type { SelectedAccount } from '@newfront-insurance/nova-core';
import type { Provider } from '@newfront-insurance/react-provision';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { v4 } from 'uuid';
import { useMarkChatAsRead } from '../../../hooks/use-mark-chat-as-read';
import { getChatPlaceholder } from '../../../lib/utils';
import type { ChatAppType } from '../../../queries/chats';
import { GET_CHAT_WITH_MESSAGES, GET_ACCOUNT, GET_CHATS_BY_FILTERS } from '../../../queries/chats';
import { ChatOverlayProvider } from '../contexts/chat-overlay-context';
import { useChatNotificationController } from '../hooks/use-chat-notification-controller';
import { useNotificationClickNavigation } from '../hooks/use-notification-click-navigation';
import { backendMessageToSDKMessage } from '../lib/chat-utils';
import { normalizeChatUuid } from '../lib/notification-helpers';
import type { ActiveChat, ChatError } from '../lib/shared-types';
const SET_CHAT_ACCOUNT_MUTATION = graphql(`
  mutation SetChatAccount($chatUuid: String!, $accountUuid: String!) {
    setChatAccount(chatUuid: $chatUuid, accountUuid: $accountUuid) {
      uuid
      account {
        uuid
        name
      }
    }
  }
`);

// Context for wrapper components to access Nova state
interface NovaWrapperState {
  isChatVisible: boolean;
  isFloatingButtonVisible: boolean;
  hasNotifications: boolean;
  handleToggle: () => void;
  handleOpen: () => void;
  handleClose: () => void;
}
const NovaWrapperContext = createContext<NovaWrapperState | undefined>(undefined);
export function useNovaWrapperState() {
  const context = useContext(NovaWrapperContext);
  if (!context) {
    throw new Error("useNovaWrapperState must be used within NovaProvider");
  }
  return context;
}
export interface NovaProviderCallbacks {
  /** Called when starting a new chat */
  onStartNewChat?: (accountContext?: {
    uuid: string;
    name: string;
  }) => void;
  /** Called when an account is selected */
  onAccountSelect?: (account: SelectedAccount) => void;
  /** Called when chat finishes */
  onFinish?: () => void;
  /** Called when chat errors */
  onError?: (error: Error) => void;
  /** Called when chat receives data */
  onData?: (dataPart: {
    type: string;
    data: unknown;
  }) => void;
  /** Called when the active chat changes (e.g., from sidebar selection) */
  onChatChange?: (chatUuid: string) => void;
}
export interface NovaProviderProps {
  children: React.ReactNode;
  userUuid: string;
  scopes?: string[];
  authProvider: Provider<AuthProviderContext>;
  isStandaloneMode: boolean;
  isSidebarHidden?: boolean;
  initialAccountUuid?: string;
  initialChatUuid?: string;
  /** App origin for filtering chats (e.g. OUTLOOK) */
  appType?: ChatAppType;
  /** Called when chat visibility changes */
  onVisibilityChange?: (isVisible: boolean) => void;
  /** Called when chat should close (for custom close behavior) */
  onClose?: () => void;
  /** Called when chat overlay becomes visible - useful for refetching data and marking as read */
  onChatOverlayVisible?: (chatUuid: string) => void;
  callbacks?: NovaProviderCallbacks;
}

/**
 * Provider that encapsulates all chat overlay logic including:
 * - State management (visibility, activeChat, accountUuid, notifications, errors, sidebar, fullscreen)
 * - GraphQL queries (chat messages, account data)
 * - Event handlers (keyboard shortcuts, account selection, new chat)
 * - Context value creation
 *
 * Exposes callbacks for customization while handling all internal logic.
 */
export function NovaProvider({
  children,
  userUuid,
  scopes,
  authProvider,
  isStandaloneMode,
  isSidebarHidden,
  initialAccountUuid,
  initialChatUuid,
  onVisibilityChange,
  onClose,
  onChatOverlayVisible,
  callbacks,
  appType
}: NovaProviderProps) {
  const {
    isMobile
  } = useIsMobile();
  const queryAccountUuid = useQuery<string | undefined>('accountUuid');

  // Internal state management
  const [isChatVisible, setIsChatVisible] = useState(isStandaloneMode ?? false);
  const [isFloatingButtonVisible] = useState(true);
  const [activeChat, setActiveChat] = useState<ActiveChat | undefined>(initialChatUuid ? {
    chatUuid: initialChatUuid,
    hasAnyMessages: false
  } : undefined);
  const [activeAccountUuid, setActiveAccountUuid] = useState<string | undefined>(initialAccountUuid);
  const [chatError, setChatError] = useState<ChatError | undefined>(undefined);
  const [additionalContext, setAdditionalContext] = useState<Record<string, unknown>>({});
  const [isSidebarOpen, setIsSidebarOpen] = useState(!isMobile && !isSidebarHidden);
  const [isFullscreen, setIsFullscreen] = useState(isStandaloneMode ?? false);
  const [selectedAccount, setSelectedAccount] = useState<SelectedAccount>(undefined);
  const [hasAnyMessages, setHasAnyMessages] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const effectiveAccountUuid = activeAccountUuid ?? queryAccountUuid;
  const chatUuid = useMemo(() => {
    return activeChat?.chatUuid || initialChatUuid || `new-chat-${v4()}`;
  }, [activeChat?.chatUuid, initialChatUuid]);
  const notificationController = useChatNotificationController({
    userUuid,
    currentChatUuid: chatUuid,
    appType
  });
  const {
    hasNotifications,
    setHasNotifications
  } = notificationController;

  // Hook to mark chats as read when opened
  const markChatAsRead = useMarkChatAsRead(userUuid, appType);
  const isNewChat = !chatUuid || chatUuid.startsWith('new-chat-');

  // Handle visibility changes
  useEffect(() => {
    onVisibilityChange?.(isChatVisible);
  }, [isChatVisible, onVisibilityChange]);
  const {
    data,
    networkStatus
  } = useApolloQuery(GET_CHAT_WITH_MESSAGES, {
    variables: {
      uuid: chatUuid
    },
    skip: !chatUuid || isNewChat,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all'
  });
  const apolloClient = useApolloClient();
  const loading = networkStatus === NetworkStatus.loading || networkStatus === NetworkStatus.setVariables && !data;
  const chatWithMessages = data?.getChat;
  useEffect(() => {
    if (chatWithMessages?.messages) {
      setHasAnyMessages(chatWithMessages.messages.length > 0);
    }
  }, [chatWithMessages?.messages]);

  // Handle chat overlay becoming visible - mark chat as read if it has unread messages
  const handleChatOverlayVisible = useCallback(() => {
    if (!chatUuid) {
      return;
    }
    const chats = apolloClient.readQuery({
      query: GET_CHATS_BY_FILTERS,
      variables: {
        userUuid,
        appType
      }
    });
    const chat = chats?.chatsList?.find(chatItem => normalizeChatUuid(chatItem.uuid ?? '') === normalizeChatUuid(chatUuid));
    // If the current chat has unread messages, mark it as read
    // The chat data is already available (optimistically added to sidebar)
    if (chat?.hasUnreadMessages) {
      markChatAsRead(normalizeChatUuid(chatUuid));
    }

    // Call the external callback if provided
    onChatOverlayVisible?.(chatUuid);
  }, [chatUuid, markChatAsRead, onChatOverlayVisible, apolloClient, userUuid, appType]);
  useEffect(() => {
    if (isChatVisible && chatUuid) {
      handleChatOverlayVisible();
    }
  }, [isChatVisible, isNewChat, chatUuid, handleChatOverlayVisible]);
  const {
    data: accountData
  } = useApolloQuery(GET_ACCOUNT, {
    variables: {
      uuid: effectiveAccountUuid as string
    },
    skip: !effectiveAccountUuid,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all'
  });
  const shouldFetchCurrentAccount = isNewChat && !effectiveAccountUuid;
  const {
    data: currentAccountData
  } = useApolloQuery(GET_ACCOUNT, {
    variables: {
      uuid: effectiveAccountUuid as string
    },
    skip: !shouldFetchCurrentAccount || !effectiveAccountUuid,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all'
  });
  const [setChatAccountMutation] = useMutation(SET_CHAT_ACCOUNT_MUTATION);
  const initialMessages = useMemo(() => chatWithMessages?.messages.map(backendMessageToSDKMessage) ?? [], [chatWithMessages?.messages]);

  // Determine which account to display (priority: chat data > stored context > current page context)
  const displayAccount = useMemo(() => {
    const account = chatWithMessages?.account ?? (effectiveAccountUuid && accountData?.getAccount ? {
      uuid: accountData.getAccount.uuid,
      name: accountData.getAccount.name
    } : null) ?? currentAccountData?.getAccount ?? null;
    return account?.uuid ? {
      uuid: account.uuid,
      name: account.name
    } : undefined;
  }, [chatWithMessages?.account, effectiveAccountUuid, accountData?.getAccount, currentAccountData?.getAccount]);

  // Compute placeholder based on account context
  const placeholder = useMemo(() => {
    const accountNameToUse = displayAccount?.name ?? accountData?.getAccount?.name;
    return getChatPlaceholder(accountNameToUse, accountData?.getAccount?.programType);
  }, [displayAccount?.name, accountData?.getAccount?.name, accountData?.getAccount?.programType]);

  // Handle close
  const handleClose = useCallback(() => {
    setIsChatVisible(false);
    onClose?.();
    callbacks?.onFinish?.();
  }, [onClose, callbacks]);

  // Handle open
  const handleOpen = useCallback(() => {
    setHasNotifications(false);
    setIsChatVisible(true);
  }, [setHasNotifications]);

  // Handle toggle
  const handleToggle = useCallback(() => {
    if (isChatVisible) {
      handleClose();
    } else {
      handleOpen();
    }
  }, [isChatVisible, handleClose, handleOpen]);

  // Keyboard shortcuts handler
  useEffect(() => {
    if (!isChatVisible) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (isSettingsOpen) {
          return setIsSettingsOpen(false);
        }
        if (isSidebarOpen) {
          return setIsSidebarOpen(false);
        }
        if (isStandaloneMode) {
          return;
        }
        if (isFullscreen) {
          setIsFullscreen(false);
        } else {
          handleClose();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleClose, isFullscreen, isStandaloneMode, isSidebarOpen, isChatVisible, isSettingsOpen]);
  useEffect(() => {
    if (!isNewChat) {
      setSelectedAccount(undefined);
    }
  }, [isNewChat]);
  const handleStartNewChat = useCallback(() => {
    const accountContext = accountData?.getAccount ? {
      uuid: accountData.getAccount.uuid,
      name: accountData.getAccount.name
    } : undefined;

    // Reset account UUID when starting a new chat (unless explicitly set via initialAccountUuid)
    // This allows the account to fall back to query param account or be explicitly selected
    setActiveAccountUuid(undefined);
    setSelectedAccount(undefined);
    setHasAnyMessages(false); // Reset message count for new chat

    // Generate a new chat UUID directly - this ensures a fresh chat every time
    const newChatUuid = `new-chat-${v4()}`;
    setActiveChat({
      chatUuid: newChatUuid,
      accountContext: isStandaloneMode ? undefined : accountContext,
      hasAnyMessages: false
    });
    callbacks?.onStartNewChat?.(accountContext ? {
      uuid: accountContext.uuid ?? '',
      name: accountContext.name ?? ''
    } : undefined);
  }, [accountData?.getAccount, isStandaloneMode, callbacks]);
  const handleAccountSelect = useCallback((account_0: SelectedAccount) => {
    setSelectedAccount(account_0);
    const accountUuidToSet = account_0?.uuid ?? undefined;
    setActiveAccountUuid(accountUuidToSet);
    setActiveChat({
      chatUuid,
      accountContext: account_0 ? {
        uuid: account_0.uuid ?? null,
        name: account_0.label
      } : {
        uuid: null
      },
      hasAnyMessages
    });

    // If chat has messages, also update the account on the backend
    // This allows setting the account after chat creation, matching the old behavior
    if (account_0?.uuid && hasAnyMessages) {
      setChatAccountMutation({
        variables: {
          chatUuid: chatUuid.replace('new-chat-', ''),
          accountUuid: account_0.uuid
        },
        optimisticResponse: {
          setChatAccount: {
            uuid: chatUuid,
            account: {
              uuid: account_0.uuid,
              name: account_0.label
            }
          }
        }
      });
    }
    callbacks?.onAccountSelect?.(account_0);
  }, [chatUuid, hasAnyMessages, setChatAccountMutation, callbacks]);
  const handleSetActiveChat = useCallback((chatUuidToSet: string) => {
    setActiveChat({
      chatUuid: chatUuidToSet,
      hasAnyMessages: true
    });
    setHasAnyMessages(false);
    callbacks?.onChatChange?.(chatUuidToSet);
  }, [callbacks]);

  // Listen for notification clicks to navigate to the chat
  useNotificationClickNavigation({
    onSelectChat: handleSetActiveChat,
    onOpenChat: () => {
      if (!isChatVisible) {
        setIsChatVisible(true);
      }
      setHasNotifications(false);
    }
  });
  const overlayContextValue = useMemo(() => ({
    isSidebarOpen,
    setIsSidebarOpen,
    isFullscreen,
    setIsFullscreen,
    accountUuid: effectiveAccountUuid,
    accountName: accountData?.getAccount?.name,
    accountProgramType: accountData?.getAccount?.programType,
    selectedAccount,
    setAccountUuid: setActiveAccountUuid,
    onSelectAccount: handleAccountSelect,
    chatUuid,
    isNewChat,
    hasAnyMessages,
    setHasAnyMessages,
    startNewChat: handleStartNewChat,
    setActiveChat: handleSetActiveChat,
    isStandaloneMode,
    isVisible: isChatVisible,
    onClose: handleClose,
    userUuid,
    userScopes: scopes,
    authProvider,
    displayAccount: displayAccount ?? undefined,
    placeholder,
    initialMessages,
    isLoadingMessages: loading,
    chatError,
    setChatError,
    hasNotifications,
    setHasNotifications,
    additionalContext,
    setAdditionalContext,
    queryAccountUuid,
    callbacks,
    notificationController,
    appType,
    isSettingsOpen,
    setIsSettingsOpen
  }), [isSidebarOpen, isFullscreen, effectiveAccountUuid, accountData?.getAccount?.name, accountData?.getAccount?.programType, selectedAccount, chatUuid, isNewChat, handleStartNewChat, handleAccountSelect, handleSetActiveChat, hasAnyMessages, setHasAnyMessages, setHasNotifications, isStandaloneMode, isChatVisible, handleClose, userUuid, scopes, authProvider, displayAccount, placeholder, initialMessages, loading, chatError, hasNotifications, additionalContext, queryAccountUuid, callbacks, notificationController, appType, isSettingsOpen, setIsSettingsOpen]);

  // Expose wrapper state for FloatingButton and other external components
  const wrapperState = useMemo<NovaWrapperState>(() => ({
    isChatVisible,
    isFloatingButtonVisible,
    hasNotifications,
    handleToggle,
    handleOpen,
    handleClose
  }), [isChatVisible, isFloatingButtonVisible, hasNotifications, handleToggle, handleOpen, handleClose]);
  return <NovaWrapperContext.Provider value={wrapperState}>
      <ChatOverlayProvider value={overlayContextValue}>{children}</ChatOverlayProvider>
    </NovaWrapperContext.Provider>;
}